import { create } from 'zustand';

import { LifecycleStatus, SubStatus } from '../models/boxLifecycle';

export type TBoxesSortBy = 'createdAt' | 'updatedAt';

type productLifecycles = { status: LifecycleStatus; subStatuses: SubStatus[] };

export interface IBox {
  id: string;
  thingName: string;
  certificateID: string;
  status: string;
  manufacturingDate: string;
  parsedManufacturingDate?: string;
  productionLineNumber: string;
  tabletSerialNumber: string;
  electronicCircuitBoard: string;
  ePaperDisplay: string;
  batterySerialNumber: string;
  bluetoothMacAddress: string;
  wifiMacAddress: string;
  imei: string;
  bleStackVersion: string;
  assemblyDone: string;
  createdAt: string;
  updatedAt: string;
  parsedCreatedAt?: string;
  parsedUpdatedAt?: string;
}

interface IBoxesStore {
  rowData: IBox[];
  pageSize: number;
  offset: number;
  refreshNonce: number;
  sortBy: TBoxesSortBy;
  order: 'ASC' | 'DESC';
  defaultSort: {
    sortBy: TBoxesSortBy;
    order: 'ASC' | 'DESC';
  };
  error: string | boolean;
  loading: boolean;
  productLifecycles?: {
    status: LifecycleStatus;
    subStatuses?: SubStatus[];
  }[];
  productDescription: String[];
  count: number;
  dispatch: (args: TBoxesReducerInput) => void;
  total: number;
  searchTerms: {
    thingName: string;
    boxId: string;
    bleMacAddress: string;
    partnerId: string;
    partnerIds: string[];
    notesSearchTerm: string;
  };
}

type TBoxesReducerInput =
  | {
      type: 'updateRowData';
      args: { rowData: any[] };
    }
  | {
      type: 'updateError';
      args: { error: string | boolean };
    }
  | {
      type: 'updateLoading';
      args: { loading: boolean };
    }
  | {
      type: 'updatePagination';
      args: { pageSize: number; offset: number };
    }
  | {
      type: 'updateSorting';
      args: {
        sortBy: TBoxesSortBy;
        order: 'ASC' | 'DESC';
      };
    }
  | {
      type: 'updateFilters';
      args: {
        productLifecycles: productLifecycles[];
        productDescription: String[];
      };
    }
  | {
      type: 'refreshTable';
    }
  | {
      type: 'updateCount';
      args: { count: number };
    }
  | {
      type: 'updateTotal';
      args: { total: number };
    }
  | {
      type: 'updateDefaultSort';
    }
  | {
      type: 'updateSearchTerms';
      args: {
        thingName: string;
        boxId: string;
        bleMacAddress: string;
        partnerId: string;
        partnerIds: string[];
        notesSearchTerm: string;
      };
    }
  | {
      type: 'reset';
    };

const boxesReducer = (state: IBoxesStore, input: TBoxesReducerInput) => {
  switch (input.type) {
    case 'updateRowData':
      return { ...state, rowData: input.args.rowData };
    case 'updateError':
      return { ...state, error: input.args.error };
    case 'updateLoading':
      return { ...state, loading: input.args.loading };
    case 'updatePagination':
      return {
        ...state,
        pageSize: input.args.pageSize,
        offset: input.args.offset,
      };
    case 'updateSorting':
      return { ...state, sortBy: input.args.sortBy, order: input.args.order };
    case 'refreshTable':
      return {
        ...state,
        refreshNonce: state.refreshNonce + 1,
      };
    case 'updateCount':
      return { ...state, count: input.args.count };
    case 'updateTotal':
      return { ...state, total: input.args.total };
    case 'updateDefaultSort':
      return {
        ...state,
        defaultSort: { sortBy: state.sortBy, order: state.order },
      };
    case 'updateSearchTerms':
      return {
        ...state,
        searchTerms: {
          ...input.args,
          ...(input.args['thingName'] && {
            thingName: input.args['thingName'].replaceAll(/[/\s+|-]/g, ''),
          }),
        },
        offset: 0,
      };

    case 'updateFilters':
      return {
        ...state,
        productLifecycles: input.args.productLifecycles,
        productDescription: input.args.productDescription,
      };
    case 'reset':
      return INITIAL_STATE;
  }
};

const INITIAL_STATE: Omit<IBoxesStore, 'dispatch'> = {
  rowData: [],
  error: '',
  loading: false,
  pageSize: 50,
  offset: 0,
  refreshNonce: 0,
  sortBy: 'updatedAt',
  order: 'DESC',
  defaultSort: {
    sortBy: 'updatedAt',
    order: 'DESC',
  },
  count: 0,
  total: 0,
  productLifecycles: [],
  productDescription: [],
  searchTerms: {
    thingName: '',
    boxId: '',
    bleMacAddress: '',
    partnerId: '',
    partnerIds: [],
    notesSearchTerm: '',
  },
};

const useBoxesStore = create<IBoxesStore>((set) => ({
  ...INITIAL_STATE,
  dispatch: (input) => set((state) => boxesReducer(state, input)),
}));

export default useBoxesStore;
